import React, { useMemo } from 'react'
import { View } from 'react-native'

import Spinner from '@common/components/Spinner'
import Text from '@common/components/Text'
import { useTranslation } from '@common/config/localization'
import useTheme from '@common/hooks/useTheme'
import spacings from '@common/styles/spacings'
import common from '@common/styles/utils/common'
import flexbox from '@common/styles/utils/flexbox'
import PendingTokenSummary from '@web/modules/sign-account-op/components/PendingTokenSummary'

import getStyles from './styles'
import { DisplayBalanceChange } from './types'

type Props = {
  changes: DisplayBalanceChange[]
  /** Balance changes are still being resolved — render the spinner card. */
  isLoading?: boolean
  /** Nothing changed AND nothing is coming: render "No balance changes detected". */
  emptyLabel?: string
}

/**
 * The "Balance changes" block of the activity detail sheet: an "Assets out" and
 * an "Assets in" card side by side. Shared by the public (SummaryDetails) and
 * the private (PrivateSummaryDetailsSheet) detail views so both render the same
 * format — see Trello #467.
 */
const BalanceChangesSection = ({ changes, isLoading = false, emptyLabel }: Props) => {
  const { styles, theme } = useTheme(getStyles)
  const { t } = useTranslation()

  const assetsOut = useMemo(() => changes.filter((change) => change.balanceChange < 0n), [changes])
  const assetsIn = useMemo(() => changes.filter((change) => change.balanceChange > 0n), [changes])
  const hasAssetBalanceChanges = !!(assetsOut.length || assetsIn.length)

  const renderCard = (title: string, cardChanges: DisplayBalanceChange[]) => (
    <View style={styles.modalSimulationContainer}>
      <View style={styles.modalSimulationContainerHeader}>
        <Text fontSize={14} weight="semiBold" appearance="secondaryText">
          {t(title)}
        </Text>
      </View>
      <View style={styles.modalSimulationBody}>
        {cardChanges.map((change, index) => (
          <PendingTokenSummary
            key={`${change.address}-${change.balanceChange.toString()}`}
            token={{
              ...change,
              simulationAmount: change.balanceChange
            }}
            chainId={change.chainId}
            hasBottomSpacing={index < cardChanges.length - 1}
          />
        ))}
      </View>
    </View>
  )

  const loadingCard = (
    <View
      style={[
        flexbox.directionRow,
        flexbox.alignCenter,
        spacings.phSm,
        spacings.pvSm,
        {
          backgroundColor: theme.secondaryBackground,
          borderWidth: 1,
          borderColor: theme.secondaryBorder,
          ...common.borderRadiusPrimary
        }
      ]}
    >
      <Spinner style={{ width: 18, height: 18 }} />
      <Text style={spacings.mlSm} fontSize={14} appearance="secondaryText">
        {t('Loading balance changes')}
      </Text>
    </View>
  )

  return (
    <View style={[styles.modalSection, spacings.pb0]} testID="activity-balance-changes-step">
      <Text appearance="tertiaryText" weight="medium" style={spacings.mbSm}>
        {t('Balance changes')}
      </Text>
      <View style={flexbox.flex1}>
        {hasAssetBalanceChanges && (
          <View style={[flexbox.directionRow, flexbox.flex1]}>
            {!!assetsOut.length && (
              <View style={[flexbox.flex1, assetsIn.length ? spacings.mrTy : undefined]}>
                {renderCard('Assets out', assetsOut)}
              </View>
            )}
            {!!assetsIn.length && (
              <View style={flexbox.flex1}>{renderCard('Assets in', assetsIn)}</View>
            )}
          </View>
        )}
        {isLoading && (
          <View style={hasAssetBalanceChanges ? spacings.mtSm : undefined}>{loadingCard}</View>
        )}
        {!isLoading && !hasAssetBalanceChanges && (
          <View
            style={[
              spacings.phSm,
              spacings.pvSm,
              {
                backgroundColor: theme.secondaryBackground,
                borderWidth: 1,
                borderColor: theme.secondaryBorder,
                ...common.borderRadiusPrimary
              }
            ]}
          >
            <Text fontSize={14} appearance="secondaryText">
              {emptyLabel || t('No balance changes detected')}
            </Text>
          </View>
        )}
      </View>
    </View>
  )
}

export default React.memo(BalanceChangesSection)
